import { Injectable } from '@angular/core';
import { clientLegalName, isGuarantor } from '@core/util/client';
import { isSet } from '@core/util/value';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { BaseClient, ClientType, PersonClient } from '@models/clients';
import { Identification } from '@models/identification';
import { LLCTransaction } from '@models/llc-mortgages';
import {
	BusinessMatterClient,
	BusinessMatterClientWithAssociations,
	Matter,
	MatterAdjustment,
	MatterClientType,
	MatterTitle,
	PersonMatterClient,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import { MatterMortgage } from '@models/mortgage';
import {
	StepMilestone,
	StepMilestoneTask,
} from 'src/app/shared/atomic/molecules/step-progress-nav/step-progress-nav.component';
import { BirthCertificateTypes, Ids } from 'src/app/shared/data/Identification';
import { LLCLenderType } from 'src/app/shared/services/mortgages/llc.service';
import { UrlService } from 'src/app/shared/services/url/url.service';
import { StepsService } from './steps.service';
import { Jurisdiction } from '@models/jurisdictions';
import {
	ClientMappings,
	MortgageInstructionPerson,
} from '@models/mortgages/mortgage-base';
import { GlobalProfileComponent } from 'src/app/private/global-profile/global-profile.component';
import { GlobalProfileNavType } from 'src/app/private/global-profile/global-profile.types';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';

@Injectable({
	providedIn: 'root',
})
export class StepCalculationsBuilder {
	constructor(
		private stepsService: StepsService,
		private drawerService: DrawerService,
	) {}

	getMortgageInformation = (matter: Matter): StepMilestone => {
		const items: StepMilestoneTask[] = [];

		items.push(this.stepsService.closingDate(matter));
		items.push(this.stepsService.propertyDetails_propertyAddress(matter));
		items.push(this.stepsService.fileNumber(matter));

		return this.buildItemsSectionToComplete('Mortgage information', items);
	};

	getBNSRequestForFundsRegistrationDetails = (
		matter: Matter,
	): StepMilestone => {
		const items: StepMilestoneTask[] = [];

		items.push(this.stepsService.propertyDetails_rollNumber(matter));

		return this.buildItemsSectionToComplete('Registration details', items);
	};

	getTDRequestForFundsRegistrationDetails = (
		matter: Matter,
		matterTitles: MatterTitle[],
	): StepMilestone => {
		const items: StepMilestoneTask[] = [];

		items.push(this.stepsService.propertyDetails_pid(matter, matterTitles));
		items.push(this.stepsService.propertyDetails_rollNumber(matter));
		items.push(
			this.stepsService.propertyDetails_legalDescription(matter, matterTitles),
		);

		return this.buildItemsSectionToComplete('Registration details', items);
	};

	getTDPropertyDetails = (
		matter: Matter,
		adjusments: MatterAdjustment[],
		matterTitles: MatterTitle[],
		jurisdiction: Jurisdiction,
	): StepMilestone => {
		const items: StepMilestoneTask[] = [];
		items.push(this.stepsService.propertyDetails_propertyAddress(matter));
		items.push(this.stepsService.propertyDetails_pid(matter, matterTitles));
		items.push(
			this.stepsService.propertyDetails_jurisdiction(jurisdiction, matter?.id),
		);
		items.push(this.stepsService.propertyDetails_condoOrStrat(matter));
		items.push(
			this.stepsService.propertyDetails_legalDescription(matter, matterTitles),
		);
		items.push(this.stepsService.propertyDetails_typeOfEstate(matter));
		this.stepsService.propertyDetails_newConstruction(
			matter,
			adjusments,
			items,
		);

		return this.buildItemsSectionToComplete('Property details', items);
	};

	getMMsFinalReportPropertyDetails = (matter: Matter): StepMilestone => {
		const items: StepMilestoneTask[] = [];
		items.push(this.stepsService.propertyDetails_propertyAddress(matter));

		return this.buildItemsSectionToComplete('Property details', items);
	};

	getBNSPropertyDetails = (
		matter: Matter,
		matterTitles: MatterTitle[],
		jurisdiction: Jurisdiction,
	): StepMilestone => {
		const items: StepMilestoneTask[] = [];
		items.push(this.stepsService.propertyDetails_propertyAddress(matter));
		items.push(this.stepsService.propertyDetails_pid(matter, matterTitles));
		items.push(
			this.stepsService.propertyDetails_jurisdiction(jurisdiction, matter?.id),
		);
		items.push(this.stepsService.propertyDetails_condoOrStrat(matter));
		items.push(
			this.stepsService.propertyDetails_legalDescription(matter, matterTitles),
		);

		return this.buildItemsSectionToComplete('Property details', items);
	};

	getTitleInsuranceDetails = (
		matter: Matter,
		effectiveDate: string,
	): StepMilestone => {
		const items: StepMilestoneTask[] = [];

		items.push(this.stepsService.titleInsurance_companyName(matter));
		items.push(
			this.stepsService.titleInsurance_policyDate(effectiveDate, matter.id),
		);
		items.push(this.stepsService.titleInsurance_policyNumber(matter));

		return this.buildItemsSectionToComplete('Title insurance details', items);
	};

	getFireInsuranceDetails = (matter: Matter): StepMilestone => {
		const items: StepMilestoneTask[] = [];

		items.push(this.stepsService.fireInsurance_companyName(matter));
		items.push(this.stepsService.fireInsurance_policyNumber(matter));
		items.push(this.stepsService.fireInsurance_insuranceAmount(matter));
		items.push(this.stepsService.fireInsurance_activeDate(matter));

		return this.buildItemsSectionToComplete('Fire insurance details', items);
	};

	getPropertyTaxDetails = (matter: Matter): StepMilestone => {
		const items: StepMilestoneTask[] = [];

		items.push(this.stepsService.propertyDetails_rollNumber(matter));

		return this.buildItemsSectionToComplete('Property tax details', items);
	};

	getFinalReportRegistrationDetails = (
		matter: Matter,
		matterMortgage: MatterMortgage,
	): StepMilestone => {
		const items: StepMilestoneTask[] = [];

		items.push(this.stepsService.closingDate(matter));

		items.push(this.stepsService.matterMortgage_regNumber(matterMortgage));
		items.push(this.stepsService.matterMortgage_regDate(matterMortgage));
		items.push(this.stepsService.propertyDetails_lro(matter));

		return this.buildItemsSectionToComplete('Registration details', items);
	};

	getBorrowersDetailsComplete = (
		matterId: string,
		clientAssociations: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		nameFormattingSettings: NameFormattingSettings,
	): StepMilestone[] =>
		clientAssociations
			.filter(
				(x) =>
					x?.clientTypes?.includes(MatterClientType.BORROWER) ||
					x?.clientTypes?.includes(MatterClientType.BUYER),
			)
			.map((client) =>
				this.getClientStep(matterId, client, nameFormattingSettings),
			);

	getClientStep(
		matterId: string,
		client:
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations,
		nameFormattingSettings: NameFormattingSettings,
	): StepMilestone {
		const clientName = clientLegalName(client, nameFormattingSettings);
		const verify =
			client?.type === ClientType.PERSON
				? this.verifyPerson(matterId, client as PersonMatterClient)
				: this.verifyCompany(matterId, client as BusinessMatterClient);
		const completed = verify.filter((x) => x.completed) ?? [];

		const step: StepMilestone = {
			title: clientName,
			itemsCompleted: completed.length,
			totalItems: verify.length,
			tasks: verify,
			completed: completed.length === verify.length,
		};

		return step;
	}

	getGuarantorsDetailsComplete = (
		matterId: string,
		clientAssociations: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		nameFormattingSettings: NameFormattingSettings,
	): StepMilestone[] =>
		clientAssociations
			.filter((x) => isGuarantor(x))
			.map((x) => this.getClientStep(matterId, x, nameFormattingSettings));

	getSigningOfficersDetailsCompleted = (
		matterId: string,
		clientAssociations: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
	): StepMilestone => {
		const items: StepMilestoneTask[] = [];

		clientAssociations
			.filter(
				(x) =>
					x?.clientTypes?.includes(MatterClientType.BORROWER) ||
					x?.clientTypes?.includes(MatterClientType.BUYER),
			)
			.forEach((parentClient) => {
				if (parentClient.type !== ClientType.PERSON) {
					const signingOffiers = parentClient?.clientAssociations?.filter(
						(x) => x?.associations?.types['signing_officer'],
					);
					if (signingOffiers.length === 0) {
						items.push(
							this.buildTaskToComplete(
								'Create signing officers',
								false,
								null,
								this.openGlobalProfileWithOptionalTab(parentClient.id),
							),
						);
					} else {
						signingOffiers?.forEach((signingOfficer) => {
							const signingOfficerClient =
								signingOfficer.client as PersonMatterClient;
							const complete =
								this.verifyPerson(matterId, signingOfficerClient).length === 0;
							const name = `${signingOfficerClient.firstName} ${signingOfficerClient.lastName}`;
							const action = this.openGlobalProfileWithOptionalTab(
								parentClient.id,
							);

							this.buildTaskToComplete(name, complete, null, action);
						});
					}
				}
			});

		return this.buildItemsSectionToComplete('Signing Officer Details', items);
	};

	getVerifiedClientIds = (
		matterId: string,
		clientAssociations: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		llcLenderType: LLCLenderType,
		nameFormattingSettings: NameFormattingSettings,
	): StepMilestone[] => {
		const items: StepMilestone[] = [];

		clientAssociations
			.filter(
				(x) =>
					(x?.clientTypes?.includes(MatterClientType.BORROWER) ||
						x?.clientTypes?.includes(MatterClientType.BUYER)) &&
					x.type === ClientType.PERSON,
			)
			.forEach((_client) => {
				const result = this.verifyClientIds(
					_client as PersonClient,
					llcLenderType,
					matterId,
					nameFormattingSettings,
				);
				items.push(result);
			});

		return items;
	};

	getVerifiedGuarantorIds = (
		matterId: string,
		matterMortgageId: string,
		clientAssociations: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		llcLenderType: LLCLenderType,
		nameFormattingSettings: NameFormattingSettings,
		guarantorsCount?: number,
	): StepMilestone[] => {
		const items: StepMilestone[] = [];

		if (
			guarantorsCount === null ||
			guarantorsCount === undefined ||
			guarantorsCount === 0
		) {
			return [];
		}

		const guarantorIds = clientAssociations.filter(
			(x) => isGuarantor(x) && x.type === ClientType.PERSON,
		);

		if (guarantorIds?.length !== guarantorsCount) {
			const item = this.buildTaskToComplete(
				'Missing guarantors',
				false,
				UrlService.Matter_Mortgages_Mortgage(matterId, matterMortgageId),
				null,
				{
					modal: 'assignSync',
				},
			);

			items.push({
				title: 'Guarantors',
				itemsCompleted: 0,
				totalItems: 1,
				tasks: [item],
				completed: false,
			});
		}

		guarantorIds.forEach((_client) => {
			const result = this.verifyClientIds(
				_client as PersonClient,
				llcLenderType,
				matterId,
				nameFormattingSettings,
			);

			items.push(result);
		});

		return items;
	};

	getVerifiedSigningOfficerIds = (
		matterId: string,
		clientAssociations: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		llcLenderType: LLCLenderType,
		nameFormattingSettings: NameFormattingSettings,
	): StepMilestone[] => {
		const items: StepMilestone[] = [];

		clientAssociations
			.filter((x) => x.type !== ClientType.PERSON)
			.forEach((businessClient) => {
				if (businessClient?.clientAssociations.length === 0) {
					const task = this.buildTaskToComplete(
						'Add signing officers to client',
						false,
						null,
						this.openGlobalProfileWithOptionalTab(businessClient.id),
					);
					items.push({
						title: `${clientLegalName(businessClient, nameFormattingSettings)} signing officers`,
						itemsCompleted: 0,
						totalItems: 1,
						tasks: [task],
						completed: false,
					});
				}

				businessClient.clientAssociations.forEach((_client) => {
					const result = this.verifyClientIds(
						_client.client as PersonClient,
						llcLenderType,
						matterId,
						nameFormattingSettings,
					);
					items.push(result);
				});
			});

		return items;
	};

	getBorrowersReconcileStatus = (
		mortgage: LLCTransaction,
		matterId: string,
		matterMortgageId: string,
		matterClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
	): StepMilestone => {
		const mappings = mortgage?.clientMappings;
		const mortgagors = mortgage?.data?.mortgagors;

		return this.checkMappings(
			'Borrower(s) reconciled',
			'mortgagorType',
			mappings,
			mortgagors,
			matterId,
			matterMortgageId,
			matterClients,
		);
	};

	getGuarantorsReconcileStatus = (
		mortgage: LLCTransaction,
		matterId: string,
		matterMortgageId: string,
		matterClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
	) => {
		if (
			!mortgage?.data?.guarantors ||
			mortgage?.data?.guarantors?.length === 0
		) {
			return null;
		}

		const mappings = mortgage?.clientMappings;
		const mortgagors = mortgage?.data?.guarantors;

		return this.checkMappings(
			'Guarantor(s) reconciled',
			'guarantorType',
			mappings,
			mortgagors,
			matterId,
			matterMortgageId,
			matterClients,
		);
	};

	private checkMappings = (
		title: string,
		type: string,
		mappings: ClientMappings,
		clients: MortgageInstructionPerson[],
		matterId: string,
		matterMortgageId: string,
		matterClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
	): StepMilestone => {
		const items: StepMilestoneTask[] = [];

		clients?.forEach((client) => {
			// companies have signing officers
			const _clientType = client[type];

			if (_clientType.toLowerCase() === 'person') {
				items.push(
					this.clientPersonExists(
						client,
						mappings,
						matterId,
						matterMortgageId,
						matterClients,
					),
				);
			} else {
				items.push(
					...this.clientBusinessExists(
						client,
						mappings,
						matterId,
						matterMortgageId,
						matterClients as BusinessMatterClientWithAssociations[],
					),
				);
			}
		});

		return this.buildItemsSectionToComplete(title, items);
	};

	clientBusinessExists = (
		client: MortgageInstructionPerson,
		mappings: ClientMappings,
		matterId: string,
		matterMortgageId: string,
		matterClients: BusinessMatterClientWithAssociations[],
	): StepMilestoneTask[] => {
		const result: StepMilestoneTask[] = [];
		let matterBusinessExists = false;
		let matterSingingOfficerExists = false;

		const _businessExists =
			mappings?.length > 0
				? mappings.find((x) => x.llcId === client.id && x.type === 'company')
				: null;

		const _signingOfficerExists =
			mappings?.length > 0
				? mappings.find(
						(x) => x.llcId === client.id && x.type === 'signingOfficer',
					)
				: null;

		if (_businessExists) {
			const business = matterClients.find(
				(x) => x.id === _businessExists.closerClientId,
			);

			if (business) {
				matterBusinessExists = true;
				if (business?.associations && _signingOfficerExists) {
					matterSingingOfficerExists = Object.entries(
						business?.associations,
					).some(
						([key, value]) =>
							key === _signingOfficerExists.closerClientId &&
							value.types['signing_officer'],
					);
				}
			}
		}

		const _businessName = client.companyName;
		const _signingOfficerName = `${client.firstName} ${client.lastName}`;

		result.push(
			this.buildTaskToComplete(
				_businessName,
				matterBusinessExists,
				UrlService.Matter_Mortgages_Mortgage(matterId, matterMortgageId),
				null,
				{ modal: 'assignSync' },
				false,
			),
		);

		if (client?.firstName?.length > 0) {
			result.push(
				this.buildTaskToComplete(
					_signingOfficerName,
					matterSingingOfficerExists,
					UrlService.Matter_Mortgages_Mortgage(matterId, matterMortgageId),
					null,
					{ modal: 'assignSync' },
					false,
				),
			);
		}

		return result;
	};

	clientPersonExists = (
		client: MortgageInstructionPerson,
		mappings: ClientMappings,
		matterId: string,
		matterMortgageId: string,
		matterClients: BaseClient[],
	): StepMilestoneTask => {
		let exists = false;

		const mappingExists =
			mappings?.length > 0
				? mappings.find((mapping) => mapping.llcId === client.id)
				: null;

		if (mappingExists) {
			exists = matterClients.some((x) => x.id === mappingExists.closerClientId);
		}

		const _clientName = `${client.firstName} ${client.lastName}`;

		return this.buildTaskToComplete(
			_clientName,
			exists,
			UrlService.Matter_Mortgages_Mortgage(matterId, matterMortgageId),
			null,
			{ modal: 'assignSync' },
			false,
		);
	};

	totalItemsCompleted = (items: StepMilestoneTask[]) =>
		items?.filter((x) => x.completed).length ?? 0;

	buildItemsSectionToComplete = (
		title: string,
		items: StepMilestoneTask[],
	): StepMilestone => {
		const itemsCompleted = this.totalItemsCompleted(items);

		return {
			title,
			itemsCompleted,
			totalItems: items.length,
			tasks: items,
			completed: itemsCompleted === items.length,
		};
	};

	buildTaskToComplete = (
		title: string,
		completed: boolean,
		url?: string,
		action?: () => void,
		queryParams?: { [id: string]: string },
		addBackButton = true,
	): StepMilestoneTask => {
		let _queryParams = { ...queryParams };

		if (addBackButton) {
			if (queryParams) {
				_queryParams['back-string'] = 'mortgage';
			} else {
				_queryParams = {
					'back-string': 'mortgage',
				};
			}
		}

		return {
			title,
			completed,
			url,
			action,
			queryParams: _queryParams,
		};
	};

	verifyPerson = (
		matterId: string,
		client: PersonMatterClient,
	): StepMilestoneTask[] => {
		const issues: StepMilestoneTask[] = [];

		issues.push(
			this.buildTaskToComplete(
				'Firstname',
				isSet(client?.firstName),
				null,
				this.openGlobalProfileWithOptionalTab(client.id),
				null,
				true,
			),
		);

		issues.push(
			this.buildTaskToComplete(
				'Lastname',
				isSet(client?.lastName),
				null,
				this.openGlobalProfileWithOptionalTab(client.id),
				null,
				true,
			),
		);

		issues.push(
			this.buildTaskToComplete(
				'Date of birth',
				isSet(client?.dateOfBirth),
				null,
				this.openGlobalProfileWithOptionalTab(client.id),
				null,
				true,
			),
		);

		issues.push(
			this.buildTaskToComplete(
				'Occupation',
				isSet(client?.occupation),
				null,
				this.openGlobalProfileWithOptionalTab(client.id),
				null,
				true,
			),
		);

		issues.push(
			this.buildTaskToComplete(
				'Address',
				isSet(client?.serviceAddress),
				null,
				this.openGlobalProfileWithOptionalTab(client.id),
				null,
				true,
			),
		);

		return issues;
	};

	verifyCompany = (
		matterId: string,
		client: BusinessMatterClient,
	): StepMilestoneTask[] => {
		const issues: StepMilestoneTask[] = [];

		issues.push(
			this.buildTaskToComplete(
				'Company name',
				isSet(client?.name),
				null,
				this.openGlobalProfileWithOptionalTab(client.id),
				null,
				true,
			),
		);

		issues.push(
			this.buildTaskToComplete(
				'Corporation number',
				isSet(client?.corporationNumber),
				null,
				this.openGlobalProfileWithOptionalTab(client.id),
				null,
				true,
			),
		);

		issues.push(
			this.buildTaskToComplete(
				'Address',
				isSet(client?.serviceAddress),
				null,
				this.openGlobalProfileWithOptionalTab(client.id),
				null,
				true,
			),
		);

		issues.push(
			this.buildTaskToComplete(
				'Jurisdiction',
				isSet(client?.jurisdiction),
				null,
				this.openGlobalProfileWithOptionalTab(client.id),
				null,
				true,
			),
		);

		issues.push(
			this.buildTaskToComplete(
				'Business activity',
				isSet(client?.businessActivity),
				null,
				this.openGlobalProfileWithOptionalTab(client.id),
				null,
				true,
			),
		);

		issues.push(
			this.buildTaskToComplete(
				'Record source',
				isSet(client?.recordSource),
				null,
				this.openGlobalProfileWithOptionalTab(client.id),
				null,
				true,
			),
		);

		return issues;
	};

	verifyClientIds = (
		client: PersonClient,
		lenderType: LLCLenderType,
		matterId: string,
		nameFormatingSettings: NameFormattingSettings,
	): StepMilestone => {
		const tasks: StepMilestoneTask[] = [];

		if (client.identification?.primary) {
			tasks.push(
				...this.verifyId(
					client.identification.primary,
					lenderType,
					client,
					matterId,
					'1st',
				),
			);
		} else {
			tasks.push(
				this.buildTaskToComplete(
					'No primary Id provided',
					false,
					null,
					this.openGlobalProfileWithOptionalTab(
						client.id,
						GlobalProfileNavType.IDENTIFICATION,
					),
					null,
					true,
				),
			);
		}

		if (client.identification?.secondary) {
			tasks.push(
				...this.verifyId(
					client.identification.secondary,
					lenderType,
					client,
					matterId,
					'2nd',
				),
			);
		} else {
			tasks.push(
				this.buildTaskToComplete(
					'No secondary Id provided',
					false,
					null,
					this.openGlobalProfileWithOptionalTab(
						client.id,
						GlobalProfileNavType.IDENTIFICATION,
					),
					null,
					true,
				),
			);
		}

		const completed = tasks.filter((x) => x.completed);

		return {
			title: `IDV - ${clientLegalName(client, nameFormatingSettings)}`,
			itemsCompleted: completed.length,
			totalItems: tasks.length,
			tasks,
			completed: completed.length === tasks.length,
		};
	};

	verifyId = (
		id: Identification,
		lenderType: LLCLenderType,
		client: BaseClient,
		matterId: string,
		priority: string,
	): StepMilestoneTask[] => {
		const result: StepMilestoneTask[] = [];
		const identificationType = Ids.find((x) => x.closerId === id.type);

		result.push(
			this.buildTaskToComplete(
				`${priority} - Type`,
				isSet(id.type),
				null,
				this.openGlobalProfileWithOptionalTab(
					client.id,
					GlobalProfileNavType.IDENTIFICATION,
				),
				null,
				true,
			),
		);

		result.push(
			this.buildTaskToComplete(
				`${priority} - Number`,
				isSet(id.number),
				null,
				this.openGlobalProfileWithOptionalTab(
					client.id,
					GlobalProfileNavType.IDENTIFICATION,
				),
				null,
				true,
			),
		);

		result.push(
			this.buildTaskToComplete(
				`${priority} - Jurisdiction`,
				isSet(id.jurisdiction),
				null,
				this.openGlobalProfileWithOptionalTab(
					client.id,
					GlobalProfileNavType.IDENTIFICATION,
				),
				null,
				true,
			),
		);

		result.push(
			this.buildTaskToComplete(
				`${priority} - Country`,
				isSet(id.country),
				null,
				this.openGlobalProfileWithOptionalTab(
					client.id,
					GlobalProfileNavType.IDENTIFICATION,
				),
				null,
				true,
			),
		);

		if (!BirthCertificateTypes.includes(identificationType?.closerId)) {
			result.push(
				this.buildTaskToComplete(
					`${priority} - Expiration date`,
					isSet(id.expirationDate),
					null,
					this.openGlobalProfileWithOptionalTab(
						client.id,
						GlobalProfileNavType.IDENTIFICATION,
					),
					null,
					true,
				),
			);
		}

		result.push(
			this.buildTaskToComplete(
				`${priority} - Date of verification`,
				isSet(id.dateOfVerification),
				null,
				this.openGlobalProfileWithOptionalTab(
					client.id,
					GlobalProfileNavType.IDENTIFICATION,
				),
				null,
				true,
			),
		);

		result.push(
			this.buildTaskToComplete(
				`${priority} - Verified by`,
				isSet(id.verifiedBy),
				null,
				this.openGlobalProfileWithOptionalTab(
					client.id,
					GlobalProfileNavType.IDENTIFICATION,
				),
				null,
				true,
			),
		);

		if (
			lenderType === LLCLenderType.BNS &&
			identificationType &&
			!isSet(identificationType?.BNSKey)
		) {
			result.push(
				this.buildTaskToComplete(
					`${priority} - BNS does not support this ID type`,
					false,
					null,
					this.openGlobalProfileWithOptionalTab(
						client.id,
						GlobalProfileNavType.IDENTIFICATION,
					),
					null,
					true,
				),
			);
		}

		if (
			lenderType === LLCLenderType.TD &&
			identificationType &&
			!isSet(identificationType.TDKey)
		) {
			result.push(
				this.buildTaskToComplete(
					`${priority} - TD does not support this ID type`,
					false,
					null,
					this.openGlobalProfileWithOptionalTab(
						client.id,
						GlobalProfileNavType.IDENTIFICATION,
					),
					null,
					true,
				),
			);
		}

		return result;
	};

	openGlobalProfileWithOptionalTab = (
		clientId: string,
		tab?: GlobalProfileNavType,
	) => {
		return () => {
			this.drawerService.openDrawer(GlobalProfileComponent, {
				title: 'Global Profile',
				data: { clientId, tab },
			});
		};
	};
}
